import { c as _c } from "react/compiler-runtime";
import { Controller, useController, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FaPlus } from "react-icons/fa6";
import { LuX } from "react-icons/lu";
import { DOSE_STATUS_CONFIG } from "@/components/dose/dose-status-badge";
import { useClinicDayField } from "@/components/measurements/use-clinic-day-field";
import { SingleProviderSelect } from "@/components/provider-select/single-select";
import Button from "@/components/ui/button";
import CollapsibleText from "@/components/ui/collapsible-text";
import { DatePicker } from "@/components/ui/date-picker";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { formatUnit } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { DoseManagementStatus } from "@/types/medication";
import { DoseFormValues, componentsAreOral } from "./dose-form-schema";
export function DoseProviderField(t0) {
  const $ = _c(10);
  const {
    label: t1,
    onValueChange
  } = t0;
  const label = t1 === undefined ? "Provider" : t1;
  const {
    control
  } = useFormContext();
  let t2;
  if ($[0] !== label) {
    t2 = <Label>{label}</Label>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onValueChange) {
    t3 = t4 => {
      const {
        field
      } = t4;
      return <SingleProviderSelect value={field.value} onValueChange={providerId => {
        if (field.value === providerId) {
          return;
        }
        field.onChange(providerId);
        onValueChange?.(providerId);
      }} placeholder="Select a provider..." allowClear={false} />;
    };
    $[2] = onValueChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== control || $[5] !== t3) {
    t4 = <Controller name="providerId" control={control} render={t3} />;
    $[4] = control;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div className="space-y-1">{t2}{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}

// Where the dose was given, as a list: a location and, optionally, how much
// was given there. Shown in place of the single location box at clinics that
// record doses by location.
function DoseLocationList(t0) {
  const $ = _c(40);
  const {
    fromChart
  } = t0;
  const {
    control,
    register,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  let t2;
  if ($[0] !== control) {
    t2 = {
      control,
      name: "locations"
    };
    $[0] = control;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t2);
  const rows = useWatch({
    control,
    name: "locations"
  }) ?? [];
  let t3;
  if ($[2] !== control) {
    t3 = {
      control,
      name: "components"
    };
    $[2] = control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const components = useWatch(t3);
  const component = components.length === 1 ? components[0] : undefined;
  const doseAmount = Number(component?.amount) || 0;
  let t4;
  if ($[4] !== component) {
    t4 = component ? formatUnit(component.unit) : "";
    $[4] = component;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const unit = t4;
  let t5;
  if ($[6] !== rows) {
    t5 = rows.reduce(_temp, 0);
    $[6] = rows;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const assigned = t5;
  let t6;
  if ($[8] !== doseAmount || $[9] !== rows) {
    t6 = doseAmount > 0 && rows.some(_temp2);
    $[8] = doseAmount;
    $[9] = rows;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const showTotal = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Label className="block">Administration Location</Label>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== errors || $[13] !== fields || $[14] !== register || $[15] !== remove || $[16] !== unit) {
    let t9;
    if ($[18] !== errors || $[19] !== fields.length || $[20] !== register || $[21] !== remove || $[22] !== unit) {
      t9 = (field, index) => <div key={field.id} className="flex w-full items-start gap-2"><div className="min-w-0 flex-1"><Input type="text" className="w-full" placeholder="E.g. Glabella, Right arm" maxLength={1000} aria-label="Location" {...register(`locations.${index}.label`)} /></div><div className="w-28 shrink-0"><Input type="text" inputMode="decimal" className="w-full" placeholder={unit || "Amount"} aria-label="Amount at this location" hasError={!!errors.locations?.[index]?.amount} errorMessage={errors.locations?.[index]?.amount?.message} {...register(`locations.${index}.amount`)} /></div>{fields.length > 1 && <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label="Remove location" onClick={() => remove(index)}><LuX className="size-4" /></Button>}</div>;
      $[18] = errors;
      $[19] = fields.length;
      $[20] = register;
      $[21] = remove;
      $[22] = unit;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t8 = fields.map(t9);
    $[12] = errors;
    $[13] = fields;
    $[14] = register;
    $[15] = remove;
    $[16] = unit;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[24] !== t8) {
    t9 = <div className="space-y-2">{t8}</div>;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== assigned || $[27] !== doseAmount || $[28] !== showTotal || $[29] !== unit) {
    t10 = showTotal && <p className="text-caption text-muted-foreground"><span>{`${assigned} of ${doseAmount} ${unit} placed`}</span></p>;
    $[26] = assigned;
    $[27] = doseAmount;
    $[28] = showTotal;
    $[29] = unit;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== fromChart) {
    t11 = fromChart && <p className="text-caption text-muted-foreground">Recorded from the face chart. Saving the chart again replaces changes made here.</p>;
    $[31] = fromChart;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== append) {
    t12 = <Button type="button" variant="ghost" size="sm" icon={FaPlus} onClick={() => append({
      label: "",
      amount: ""
    })}>Add location</Button>;
    $[33] = append;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t9) {
    t13 = <div className="space-y-1">{t7}{t9}{t10}{t11}{t12}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
}
function _temp2(row_0) {
  return row_0.amount.trim();
}
function _temp(total, row) {
  return total + (Number(row.amount) || 0);
}
export function DoseEventFields(t0) {
  const $ = _c(67);
  const {
    commentLabel,
    locationList: t1,
    locationsFromChart: t2
  } = t0;
  const locationList = t1 === undefined ? false : t1;
  const locationsFromChart = t2 === undefined ? false : t2;
  const clock = useClinicClock();
  const {
    control,
    register,
    formState: t3
  } = useFormContext();
  const {
    errors
  } = t3;
  let t4;
  if ($[0] !== control) {
    t4 = {
      control,
      name: "components"
    };
    $[0] = control;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const components = useWatch(t4);
  let t5;
  if ($[2] !== control) {
    t5 = {
      control,
      name: "doseDate"
    };
    $[2] = control;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    field: t6
  } = useController(t5);
  const {
    value: doseDate,
    onChange: setDoseDate
  } = t6;
  const doseDay = useClinicDayField(doseDate, setDoseDate);
  let t7;
  if ($[4] !== control) {
    t7 = {
      control,
      name: "comment"
    };
    $[4] = control;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const comment = useWatch(t7);
  let t8;
  if ($[6] !== components) {
    t8 = componentsAreOral(components);
    $[6] = components;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const allComponentsAreOral = t8;
  let t9;
  if ($[8] !== allComponentsAreOral || $[9] !== locationList || $[10] !== locationsFromChart) {
    t9 = !allComponentsAreOral && locationList && <DoseLocationList fromChart={locationsFromChart} />;
    $[8] = allComponentsAreOral;
    $[9] = locationList;
    $[10] = locationsFromChart;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== allComponentsAreOral || $[13] !== errors.administrationLocation || $[14] !== locationList || $[15] !== register) {
    t10 = !allComponentsAreOral && !locationList && <div className="space-y-1"><Label htmlFor="administrationLocation">Administration Location</Label><Input id="administrationLocation" type="text" className="w-full" placeholder="E.g. Right arm, Left thigh" aria-invalid={!!errors.administrationLocation} hasError={!!errors.administrationLocation} errorMessage={errors.administrationLocation?.message} {...register("administrationLocation")} /></div>;
    $[12] = allComponentsAreOral;
    $[13] = errors.administrationLocation;
    $[14] = locationList;
    $[15] = register;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Label htmlFor="frequency">Frequency (Sig)</Label>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = !!errors.frequency;
  const t13 = !!errors.frequency;
  const t14 = errors.frequency?.message;
  let t15;
  if ($[18] !== register) {
    t15 = register("frequency");
    $[18] = register;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t12 || $[21] !== t13 || $[22] !== t14 || $[23] !== t15) {
    t16 = <div className="space-y-1">{t11}<Input id="frequency" type="text" className="w-full" placeholder="E.g. Daily, Twice daily, As needed" aria-invalid={t12} hasError={t13} errorMessage={t14} {...t15} /></div>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Label htmlFor="sideEffects">Side Effects</Label>;
    t18 = <p className="text-caption text-muted-foreground">Record side effects the patient is currently experiencing, not potential medication side effects.</p>;
    $[25] = t17;
    $[26] = t18;
  } else {
    t17 = $[25];
    t18 = $[26];
  }
  const t19 = !!errors.sideEffects;
  const t20 = !!errors.sideEffects;
  const t21 = errors.sideEffects?.message;
  let t22;
  if ($[27] !== register) {
    t22 = register("sideEffects");
    $[27] = register;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== t19 || $[30] !== t20 || $[31] !== t21 || $[32] !== t22) {
    t23 = <div>{t17}{t18}<Input id="sideEffects" type="text" className="mt-1 w-full" placeholder="E.g. Nausea, headache, fatigue" aria-invalid={t19} hasError={t20} errorMessage={t21} {...t22} /></div>;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Label htmlFor="managementStatus">Status</Label>;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== control) {
    t25 = <div className="space-y-1">{t24}<Controller name="managementStatus" control={control} render={_temp4} /></div>;
    $[35] = control;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== allComponentsAreOral || $[38] !== clock || $[39] !== doseDate || $[40] !== doseDay) {
    t26 = !allComponentsAreOral && <CollapsibleText text="Set a different date" open={doseDate ? clock.dayOf(doseDate) !== clock.today() : false}><div className="space-y-1"><Label htmlFor="doseDate">Dose Date</Label><DatePicker value={doseDay.value} onChange={doseDay.onChange} placeholder="Select dose date" enableYearNavigation={true} /></div></CollapsibleText>;
    $[37] = allComponentsAreOral;
    $[38] = clock;
    $[39] = doseDate;
    $[40] = doseDay;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  const t27 = `Add a ${commentLabel}`;
  const t28 = !!comment;
  let t29;
  if ($[42] !== commentLabel) {
    t29 = <Label htmlFor="comment">{commentLabel}</Label>;
    $[42] = commentLabel;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  const t30 = `Enter any additional ${commentLabel} about this dose`;
  const t31 = !!errors.comment;
  const t32 = !!errors.comment;
  const t33 = errors.comment?.message;
  let t34;
  if ($[44] !== register) {
    t34 = register("comment");
    $[44] = register;
    $[45] = t34;
  } else {
    t34 = $[45];
  }
  let t35;
  if ($[46] !== t30 || $[47] !== t31 || $[48] !== t32 || $[49] !== t33 || $[50] !== t34) {
    t35 = <Input id="comment" type="text" className="w-full" placeholder={t30} aria-invalid={t31} hasError={t32} errorMessage={t33} {...t34} />;
    $[46] = t30;
    $[47] = t31;
    $[48] = t32;
    $[49] = t33;
    $[50] = t34;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] !== t29 || $[53] !== t35) {
    t36 = <div className="space-y-1">{t29}{t35}</div>;
    $[52] = t29;
    $[53] = t35;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  let t37;
  if ($[55] !== t27 || $[56] !== t28 || $[57] !== t36) {
    t37 = <CollapsibleText text={t27} open={t28}>{t36}</CollapsibleText>;
    $[55] = t27;
    $[56] = t28;
    $[57] = t36;
    $[58] = t37;
  } else {
    t37 = $[58];
  }
  let t38;
  if ($[59] !== t10 || $[60] !== t16 || $[61] !== t23 || $[62] !== t25 || $[63] !== t26 || $[64] !== t37 || $[65] !== t9) {
    t38 = <>{t9}{t10}{t16}{t23}{t25}{t26}{t37}</>;
    $[59] = t10;
    $[60] = t16;
    $[61] = t23;
    $[62] = t25;
    $[63] = t26;
    $[64] = t37;
    $[65] = t9;
    $[66] = t38;
  } else {
    t38 = $[66];
  }
  return t38;
}
function _temp4(t0) {
  const {
    field
  } = t0;
  return <Select value={field.value ?? undefined} onValueChange={value => field.onChange(value as DoseManagementStatus)}><SelectTrigger id="managementStatus"><SelectValue placeholder="Select status" /></SelectTrigger><SelectContent>{Object.values(DoseManagementStatus).map(_temp3)}</SelectContent></Select>;
}
function _temp3(status) {
  const config = DOSE_STATUS_CONFIG[status];
  const Icon = config.icon;
  return <SelectItem key={status} value={status}><span className="flex items-center gap-2"><Icon className={cx("size-4", config.color)} />{config.label}</span></SelectItem>;
}